import React, { useCallback, useEffect, useRef, useState } from "react";
import { searchCities } from "../utils/cities";
import NeonTextBox from "./NeonTextBox";

const FULL_MAP_WIDTH_PX = 1536; // baseline used by projection formula
const FULL_MAP_HEIGHT_PX = 1024;

const CitySelector = ({ onDone, updateCity, isPartnerForm = false }) => {
  const [infoCardData, setInfoCardData] = useState({});

  // Load data from localStorage on component mount
  useEffect(() => {
    const loadDataFromLocalStorage = () => {
      try {
        const storageKey = isPartnerForm ? 'wooju_partner_profile_data' : 'wooju_profile_data';
        const savedData = localStorage.getItem(storageKey);

        if (savedData) {
          const parsedData = JSON.parse(savedData);

          // Ensure we have the complete data structure
          const completeData = {
            name: parsedData.name || "",
            birthDate: parsedData.birthDate || null,
            birthLocation: parsedData.birthLocation || null,
            unknownTime: parsedData.unknownTime || false,
            gender: parsedData.gender || "F",
          };

          setInfoCardData(completeData);
        } else {
          setInfoCardData({
            name: "",
            birthDate: null,
            birthLocation: null,
            unknownTime: false,
            gender: "F",
          });
        }
      } catch (error) {
        console.error('Error loading data from localStorage:', error);
        // Set default data on error
        setInfoCardData({
          name: "",
          birthDate: null,
          birthLocation: null,
          unknownTime: false,
          gender: "F",
        });
      }
    };

    loadDataFromLocalStorage();
  }, []);

  const storedCity = infoCardData.birthLocation; // persisted value
  const [search, setSearch] = useState(storedCity ? storedCity.name : "");
  const inputRef = useRef(null);
  const [cancelHover, setCancelHover] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  // Ephemeral selection (only committed on Select)
  const [tempCity, setTempCity] = useState(storedCity || null);

  // Marker position in displayed-map pixels
  const [mapX, setMapX] = useState(0);
  const [mapY, setMapY] = useState(0);

  // Panning infrastructure
  const containerRef = useRef(null);
  const mapImgRef = useRef(null);
  const [panX, setPanX] = useState(0); // how much we've panned to the left (in px)
  const [autoPan, setAutoPan] = useState(true); // idle animation on/off
  const [imageLoaded, setImageLoaded] = useState(false); // track if map image is loaded
  const [imageError, setImageError] = useState(false); // track if image failed to load
  const [animationSupported, setAnimationSupported] = useState(true); // check if animations are supported

  const filteredCities = search.length >= 3 ? searchCities(search) : [];

  // Check for animation support
  useEffect(() => {
    const testAnimation = () => {
      try {
        const testEl = document.createElement('div');
        testEl.style.animation = 'none';
        const supported = testEl.style.animation !== undefined;
        setAnimationSupported(supported);
      } catch (e) {
        setAnimationSupported(false);
      }
    };
    testAnimation();
  }, []);

  // Projection functions from lat/lon -> map pixel coords based on baseline
  const projectX = (lat, lon) => 0.01654 * lat + 3.29955 * lon + 702.17;
  const projectY = (lat, lon) => -3.90416 * lat + 0.00115 * lon + 573.12;

  const measure = useCallback(() => {
    const container = containerRef.current;
    const img = mapImgRef.current;
    if (!container || !img || !imageLoaded || imageError) return;

    try {
      const containerWidth = container.clientWidth;
      const imgWidth = img.clientWidth; // actual rendered width of the map image
      const distance = Math.max(0, imgWidth - containerWidth);
      // Clamp current pan within new distance
      setPanX((prev) => Math.min(Math.max(prev, 0), distance));
    } catch (error) {
      console.warn('Error measuring map dimensions:', error);
    }
  }, [imageLoaded, imageError]);

  useEffect(() => {
    if (imageLoaded && !imageError) {
      measure();
    }
  }, [measure, imageLoaded, imageError]);

  useEffect(() => {
    const onResize = () => {
      if (imageLoaded && !imageError) {
        measure();
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [measure, imageLoaded, imageError]);

  const handleCityClick = (city) => {
    // Only set temporary selection; do NOT persist yet
    setTempCity(city);
    setSearch(city.name);

    // Force a re-render to ensure state is updated
    setTimeout(() => {
      updateMapPosition(city);
    }, 0);
  };

  const updateMapPosition = (city) => {
    // Determine current displayed map width to translate baseline pixels
    const img = mapImgRef.current;
    const container = containerRef.current;
    if (!img || !container || !imageLoaded || imageError) {
      console.warn('Missing required elements for city click:', { img: !!img, container: !!container, imageLoaded, imageError });
      return;
    }

    try {
      // Safari-specific: Use offsetWidth/offsetHeight for more reliable dimensions
      const displayedMapWidth = img.offsetWidth || img.clientWidth;
      const displayedMapHeight = img.offsetHeight || img.clientHeight;
      const containerWidth = container.offsetWidth || container.clientWidth;

      if (!displayedMapWidth || !displayedMapHeight || !containerWidth) {
        console.warn('Invalid dimensions:', { displayedMapWidth, displayedMapHeight, containerWidth });
        return;
      }

      const scaleX = displayedMapWidth / FULL_MAP_WIDTH_PX;
      const scaleY = displayedMapHeight / FULL_MAP_HEIGHT_PX;

      const px = projectX(city.latitude, city.longitude) * scaleX;
      const py = projectY(city.latitude, city.longitude) * scaleY;

      // Force state updates with explicit values
      setMapX(Math.round(px * 100) / 100); // Round to 2 decimal places
      setMapY(Math.round(py * 100) / 100);

      // Compute horizontal pan to reveal/center the selected city
      const desiredLeftShift = px - containerWidth / 2; // shift map left so city is centered
      const clampedShift = Math.max(Math.min(desiredLeftShift, displayedMapWidth - containerWidth), 0);
      setAutoPan(false); // pause idle animation
      setPanX(clampedShift);

      // Force a re-render after state updates
      setTimeout(() => {
        // Current map state updated
      }, 100);
    } catch (error) {
      console.warn('Error handling city click:', error);
    }

    if (inputRef.current) inputRef.current.blur();
  };

  // Reset marker/pan when starting a new search
  const resetView = () => {
    setMapX(0);
    setMapY(0);
    setPanX(0);
    setAutoPan(true);
  };

  // Handle image load
  const handleImageLoad = () => {
    // Safari-specific: Add a small delay to ensure image is fully rendered
    setTimeout(() => {
      setImageLoaded(true);
      setImageError(false);
      measure();
    }, 50);
  };

  // Handle image error
  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
    console.warn('Failed to load map image');
  };

  // If the component remounts or key changes, ensure the default idle pan starts
  useEffect(() => {
    // Start idle pan on mount after layout
    const t = setTimeout(() => {
      if (!imageError) {
        resetView();
      }
    }, 100); // Increased delay to ensure image is loaded
    return () => clearTimeout(t);
  }, [imageLoaded, imageError]);

  const animDistance = Math.max(80, 200); // Use a reasonable default for idle animation

  // Fallback for when image fails to load
  if (imageError) {
    return (
      <div style={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "white",
        padding: "20px"
      }}>
        <div style={{ textAlign: "center" }}>
          <h3>Map Unavailable</h3>
          <p>Please check your connection and try again.</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "1px solid white",
              color: "white",
              padding: "10px 20px",
              borderRadius: "5px",
              cursor: "pointer"
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        maxWidth: "100vw",
        maxHeight: "100dvh",
      }}
    >
      {/* Inject dynamic keyframes for horizontal panning */}
      <style>{`
        @keyframes cityMapPan {
          0% { transform: translateX(0px); }
          100% { transform: translateX(-${animDistance}px); }
        }
        
        .map-pan-container {
          will-change: transform;
          transition: transform 900ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        
        .map-pan-container.auto-pan {
          animation: ${animationSupported ? `cityMapPan 29s linear infinite alternate` : 'none'};
        }
        
        .map-pan-container.manual-pan {
          animation: none;
        }
        
        /* Fallback for devices that don't support animations */
        @media (prefers-reduced-motion: reduce) {
          .map-pan-container.auto-pan {
            animation: none;
          }
        }
      `}</style>

      {/* Full-screen map layer with left-right pan */}
      <div
        ref={containerRef}
        className="map-container"
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
        }}
      >
        <div
          className={`map-pan-container ${autoPan ? 'auto-pan' : 'manual-pan'}`}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            height: "100%",
            transform: autoPan ? undefined : `translateX(-${panX}px)`,
          }}
        >
          <img
            ref={mapImgRef}
            src="/images/world_map_v2.png"
            alt="world map"
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{
              height: "100%",
              width: "auto",
              minWidth: "120%", // ensure horizontal overflow so idle pan is visible on all screens
              display: "block",
              filter: "saturate(0.9) brightness(0.9)",
            }}
          />
          {/* Marker inside the moving layer so it pans with the map */}
          <img
            src="/images/map_marker_v3.png"
            alt="map marker"
            style={{
              position: "absolute",
              top: mapY - 125 / 4 / 2,
              left: mapX - 50 / 4,
              width: 100 / 4,
              height: 125 / 4,
              display: (mapX > 0 || mapY > 0) ? "block" : "none",
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))",
              transition: animationSupported ? "top 0.9s cubic-bezier(0.22, 1, 0.36, 1), left 0.9s cubic-bezier(0.22, 1, 0.36, 1)" : "none",
              zIndex: 10,
            }}
          />
        </div>
      </div>

      {/* Overlay UI */}
      <div
        style={{
          position: "relative",
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          padding: "16px 8px 0 8px",
          paddingTop: "calc(var(--safe-area-top) + 80px)",
          background: "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.2) 25%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0) 100%)",
        }}
      >
        <div
          style={{
            width: "100%",
            padding: "0 16px",
            boxSizing: "border-box",
            display: "flex",
            maxWidth: "100%",
          }}
        >
          <NeonTextBox
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              // Do not clear persisted value on typing; clear only temp selection
              setTempCity(null);
              resetView();
            }}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
            placeholder="Type city name..."
            inputRef={inputRef}
          />
        </div>
        {search.length >= 3 && isInputFocused && (
          <div
            style={{
              width: "100%",
              marginTop: 16,
              marginLeft: 8,
              marginRight: 8,
              background: "rgba(20,10,30,0.8)",
              borderRadius: 10,
              boxShadow: "0 0 8px rgba(132, 209, 199, 0.25)",
              maxHeight: "min(35dvh, 280px)",
              WebkitOverflowScrolling: "touch",
              overflowY: "auto",
              marginBottom: 12,
            }}
          >
            {filteredCities.length === 0 ? (
              <div
                style={{
                  padding: "12px 18px",
                  color: "#fff",
                  fontSize: "1.1em",
                  cursor: "pointer",
                  transition: "background 0.2s, color 0.2s",
                }}
              >
                No results
              </div>
            ) : (
              filteredCities.map((city) => (
                <div
                  key={city.geonameid}
                  style={{
                    padding: "12px 18px",
                    color:
                      tempCity && tempCity.name === city.name
                        ? "#84D1C7"
                        : "#fff",
                    fontSize: "1.1em",
                    cursor: "pointer",
                    transition: "background 0.2s, color 0.2s",
                    background:
                      tempCity && tempCity.name === city.name
                        ? "rgba(132, 209, 199, 0.2)"
                        : undefined,
                    fontWeight:
                      tempCity && tempCity.name === city.name
                        ? "bold"
                        : undefined,
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    MozUserSelect: "none",
                    msUserSelect: "none",
                    minHeight: "44px",
                    display: "flex",
                    alignItems: "center",
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCityClick(city);
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    handleCityClick(city);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleCityClick(city);
                  }}
                >
                  {city.name},{" "}
                  {city.country_code == "US" ? city.admin1_code + ", " : ""}
                  {city.country_code}
                </div>
              ))
            )}
          </div>
        )}

        <div
          style={{
            marginTop: "auto",
            marginBottom: "calc(var(--safe-area-bottom) + 120px)",
            padding: "16px 8px 0 8px",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <button
            style={{
              background: tempCity ? "#84D1C7" : "#444",
              color: tempCity ? "#000" : "#aaa",
              border: "none",
              borderRadius: 24,
              padding: "12px 24px",
              fontSize: "1.1em",
              fontFamily: "inherit",
              boxShadow: tempCity
                ? "0 0 16px rgba(132, 209, 199, 0.9), 0 0 32px rgba(132, 209, 199, 0.45)"
                : "none",
              cursor: tempCity ? "pointer" : "not-allowed",
              transition: "background 0.2s, color 0.2s",
              minWidth: "120px",
              maxWidth: "200px",
            }}
            onClick={async () => {
              if (tempCity) {
                // Update the city in the parent InfoCard component
                if (updateCity) {
                  updateCity(tempCity);
                }

                if (onDone) onDone();
              }
            }}
            disabled={!tempCity}
          >
            Select
          </button>
          <button
            style={{
              background: cancelHover ? "rgba(132, 209, 199, 0.15)" : "transparent",
              color: "#0e3430",
              border: "1px solid #84D1C7",
              borderRadius: 24,
              padding: "12px 24px",
              fontSize: "1.1em",
              fontFamily: "inherit",
              cursor: "pointer",
              transition: "background 0.2s, color 0.2s",
              minWidth: "120px",
              maxWidth: "200px",
            }}
            onClick={() => {
              // Do not persist changes; simply close
              if (onDone) onDone();
            }}
            onMouseEnter={() => setCancelHover(true)}
            onMouseLeave={() => setCancelHover(false)}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CitySelector;
